import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiBook, FiVideo, FiAward, FiUser, FiBarChart2, FiPlay, FiDownload, 
  FiStar, FiTrendingUp, FiClock, FiCheckCircle, FiFilter, FiSearch, FiEye,
  FiThumbsUp, FiFlag, FiMessageSquare, FiBookOpen, FiTarget, FiImage
} from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getLearnerMenu } from '../../components/layout/menuConfigs';
import EducationalParticles from '../../components/common/EducationalParticles';
import VideoViewer from '../../components/common/VideoViewer';
import FileViewer from '../../components/common/FileViewer';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import CourseCard from '../../components/common/CourseCard';
import PurchaseCourseModal from '../../components/common/PurchaseCourseModal';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { useCourseOwnership } from '../../contexts/CourseOwnershipContext';
import { 
  learnerCourseAPI,
  learnerUtilityAPI,
  learnerRatingAPI,
  learnerFlagAPI,
  learnerDownloadAPI,
} from '../../api/learnerEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader, 
  StatsGrid, 
  StatCard, 
  StatIcon, 
  StatInfo, 
  SectionTitle,
  EmptyState,
  FilterBar,
  FilterSelect,
  SearchInput,
  ContentGrid,
} from '../dashboards/Dashboard.styles';

const CourseBrowsePage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { handlePurchaseSuccess } = useCourseOwnership();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [pagination, setPagination] = useState({});
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showVideoViewer, setShowVideoViewer] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [courseToPurchase, setCourseTourchase] = useState(null);
  
  const [filters, setFilters] = useState({
    search: '',
    grade_id: '',
    subject_id: '',
    teacher_id: '',
    sort_by: 'created_at',
    sort_order: 'desc',
  });
  
  const [ratingForm, setRatingForm] = useState({
    rating: 5,
    review: '',
  });
  
  const [flagForm, setFlagForm] = useState({
    reason: '',
  });

  const menuItems = getLearnerMenu();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [gradesRes, subjectsRes] = await Promise.all([
        learnerUtilityAPI.getGrades(),
        learnerUtilityAPI.getSubjects(),
      ]);
      setGrades(gradesRes.data.grades || []);
      setSubjects(subjectsRes.data.subjects || []);
    } catch (error) {
      showToast('error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await learnerCourseAPI.getCourses(filters);
      const coursesData = response.data.courses || [];
      setCourses(coursesData);
      setPagination(response.data.pagination || {});
    } catch (error) {
      showToast('error', 'Failed to load courses');
    }
  };

  const handleCourseClick = async (courseId) => {
    try {
      const response = await learnerCourseAPI.getCourseDetails(courseId);
      setSelectedCourse(response.data.course);
      setShowCourseModal(true);
    } catch (error) {
      showToast('error', 'Failed to load course details');
    }
  };

  const handleStartCourse = async (courseId) => {
    try {
      setSubmitting(true);
      await learnerCourseAPI.startCourse(courseId);
      showToast('success', 'Course started! Your progress will be tracked.');
      setShowCourseModal(false);
      fetchCourses();
    } catch (error) {
      showToast('error', 'Failed to start course');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRateCourse = async () => {
    if (!selectedCourse) return;
    
    try {
      setSubmitting(true);
      await learnerCourseAPI.rateCourse(
        selectedCourse.id, 
        ratingForm.rating, 
        ratingForm.review
      );
      showToast('success', 'Rating submitted successfully!');
      setShowRatingModal(false);
      setRatingForm({ rating: 5, review: '' });
      fetchCourses();
    } catch (error) {
      showToast('error', 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFlagCourse = async () => {
    if (!selectedCourse || !flagForm.reason.trim()) return;
    
    try {
      setSubmitting(true);
      await learnerCourseAPI.flagCourse(selectedCourse.id, flagForm.reason);
      showToast('success', 'Content flagged successfully. Our team will review it.');
      setShowFlagModal(false);
      setFlagForm({ reason: '' });
    } catch (error) {
      showToast('error', 'Failed to flag content');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWatchVideo = (video) => {
    setSelectedVideo(video);
    setShowVideoViewer(true);
  };

  const handlePreviewFile = (file) => {
    setSelectedFile(file);
    setShowFileViewer(true);
  };

  const renderStars = (rating, interactive = false, onRatingChange = null) => {
    return (
      <div style={{ display: 'flex', gap: '2px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onRatingChange && onRatingChange(star)}
            style={{
              background: 'none',
              border: 'none',
              cursor: interactive ? 'pointer' : 'default',
              padding: '2px',
            }}
          >
            <FiStar
              size={interactive ? 16 : 12}
              color={star <= rating ? '#F59E0B' : '#E5E7EB'}
              fill={star <= rating ? '#F59E0B' : 'none'}
            />
          </button>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading courses..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <EducationalParticles />
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Browse Courses 📚</h1>
            <p>Discover amazing courses and start your learning journey</p>
          </div>
        </DashboardHeader>

        {/* Filters */}
        <FilterBar>
          <SearchInput
            placeholder="Search courses..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <FilterSelect
            value={filters.grade_id}
            onChange={(e) => setFilters({ ...filters, grade_id: e.target.value })}
          >
            <option value="">All Grades</option>
            {grades.map((grade) => (
              <option key={grade.id} value={grade.id}>{grade.name}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={filters.subject_id}
            onChange={(e) => setFilters({ ...filters, subject_id: e.target.value })}
          >
            <option value="">All Subjects</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>{subject.name}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={filters.sort_by}
            onChange={(e) => setFilters({ ...filters, sort_by: e.target.value })}
          >
            <option value="created_at">Sort by Date</option>
            <option value="title">Sort by Title</option>
          </FilterSelect>
          <FilterSelect
            value={filters.sort_order}
            onChange={(e) => setFilters({ ...filters, sort_order: e.target.value })}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </FilterSelect>
        </FilterBar>

        {/* Courses Grid */}
        {courses.length === 0 ? (
          <EmptyState>
            <FiBook size={48} />
            <h3>No courses found</h3>
            <p>Try adjusting your search criteria or browse all courses</p>
            <Button onClick={() => setFilters({ ...filters, search: '', grade_id: '', subject_id: '' })}>
              Clear Filters
            </Button>
          </EmptyState>
        ) : (
          <ContentGrid>
            {courses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                showProgress={false}
                showRating={true}
                showInstructor={true}
                showGrade={true}
                showPrice={true}
                showPurchaseButton={true}
                onPurchaseClick={(course) => {
                  setCourseTourchase(course);
                  setShowPurchaseModal(true);
                }}
              />
            ))}
          </ContentGrid>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '8px', 
            marginTop: '30px' 
          }}>
            <Button 
              variant="ghost"
              disabled={pagination.page === 1}
              onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
            >
              Previous
            </Button>
            <span style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '0 16px',
              color: theme.colors.textSecondary 
            }}>
              Page {pagination.page} of {pagination.pages}
            </span>
            <Button 
              variant="ghost"
              disabled={pagination.page === pagination.pages}
              onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
            >
              Next
            </Button>
          </div>
        )}
      </DashboardContainer>

      {/* Course Details Modal */}
      <Modal
        isOpen={showCourseModal}
        onClose={() => setShowCourseModal(false)}
        title={selectedCourse?.title || 'Course Details'}
        size="large"
      >
        {selectedCourse && (
          <div style={{ padding: '20px 0' }}>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ margin: '0 0 16px 0', fontSize: '16px', lineHeight: '1.6' }}>
                {selectedCourse.description}
              </p>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '16px',
                marginBottom: '20px'
              }}>
                <div>
                  <strong>Teacher:</strong> {selectedCourse.teacher?.username}
                </div>
                <div>
                  <strong>Grade:</strong> {selectedCourse.grade?.name}
                </div>
                <div>
                  <strong>Pass Mark:</strong> {selectedCourse.pass_mark}%
                </div>
                <div>
                  <strong>Rating:</strong> {selectedCourse.average_rating?.toFixed(1) || 'No rating'} 
                  ({selectedCourse.rating_count || 0} reviews)
                </div>
              </div>
            </div>

            {/* Course Content */}
            {selectedCourse.videos && selectedCourse.videos.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>Videos ({selectedCourse.videos.length})</h4>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {selectedCourse.videos.map((video, index) => (
                    <div key={video.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: `1px solid ${theme.colors.border}`,
                    }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{video.title}</div>
                        <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                          {Math.floor(video.duration / 60)} minutes
                        </div>
                      </div>
                      <Button 
                        size="small" 
                        variant="ghost"
                        onClick={() => handleWatchVideo(video)}
                        style={{ marginRight: '8px' }}
                      >
                        <FiPlay />
                      </Button>
                      {video.is_downloadable && (
                        <Button size="small" variant="ghost">
                          <FiDownload />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedCourse.files && selectedCourse.files.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>Files ({selectedCourse.files.length})</h4>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {selectedCourse.files.map((file, index) => (
                    <div key={file.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: `1px solid ${theme.colors.border}`,
                    }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{file.title}</div>
                        <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                          {file.file_type?.toUpperCase()} • {(file.file_size / 1024 / 1024).toFixed(1)} MB
                        </div>
                      </div>
                      <Button 
                        size="small" 
                        variant="ghost"
                        onClick={() => handlePreviewFile(file)}
                        style={{ marginRight: '8px' }}
                      >
                        <FiEye />
                      </Button>
                      {file.is_downloadable && (
                        <Button size="small" variant="ghost">
                          <FiDownload />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedCourse.quizzes && selectedCourse.quizzes.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>Quizzes ({selectedCourse.quizzes.length})</h4>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {selectedCourse.quizzes.map((quiz, index) => (
                    <div key={quiz.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: `1px solid ${theme.colors.border}`,
                    }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{quiz.title}</div>
                        <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                          {quiz.question_count} questions • Pass: {quiz.pass_mark}%
                          {quiz.best_score && ` • Best: ${quiz.best_score}%`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              marginTop: '20px'
            }}>
              <Button 
                variant="ghost" 
                onClick={() => setShowRatingModal(true)}
              >
                <FiStar /> Rate Course
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => setShowFlagModal(true)}
                style={{ color: '#EF4444' }}
              >
                <FiFlag /> Flag Content
              </Button>
              {!selectedCourse.started ? (
                <Button onClick={() => handleStartCourse(selectedCourse.id)} loading={submitting}>
                  <FiPlay /> Start Course
                </Button>
              ) : (
                <Button onClick={() => navigate(`/learner/courses/${selectedCourse.id}`)}>
                  <FiBookOpen /> Continue Learning
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Rating Modal */}
      <Modal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        title="Rate Course"
        size="medium"
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Rating
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {renderStars(ratingForm.rating, true, (rating) => 
                setRatingForm({ ...ratingForm, rating })
              )}
              <span style={{ color: theme.colors.textSecondary }}>
                {ratingForm.rating} out of 5
              </span>
            </div>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Review (Optional)
            </label>
            <textarea
              value={ratingForm.review}
              onChange={(e) => setRatingForm({ ...ratingForm, review: e.target.value })}
              placeholder="Share your thoughts about this course..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px',
                border: `2px solid ${theme.colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
              }}
            />
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <Button 
              variant="ghost" 
              onClick={() => setShowRatingModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleRateCourse} loading={submitting}>
              Submit Rating
            </Button>
          </div>
        </div>
      </Modal>

      {/* Flag Modal */}
      <Modal
        isOpen={showFlagModal}
        onClose={() => setShowFlagModal(false)}
        title="Flag Content"
        size="medium"
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Reason for Flagging *
            </label>
            <textarea
              value={flagForm.reason}
              onChange={(e) => setFlagForm({ ...flagForm, reason: e.target.value })}
              placeholder="Please provide a detailed reason for flagging this content (minimum 10 characters)..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                border: `2px solid ${theme.colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
              }}
              required
            />
            <div style={{ 
              fontSize: '12px', 
              color: theme.colors.textSecondary,
              marginTop: '4px'
            }}>
              {flagForm.reason.length}/10 characters minimum
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <Button 
              variant="ghost" 
              onClick={() => setShowFlagModal(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleFlagCourse} 
              loading={submitting}
              disabled={flagForm.reason.length < 10}
              style={{ background: '#EF4444' }}
            >
              Flag Content
            </Button>
          </div>
        </div>
      </Modal>

      {/* Video Viewer */}
      <VideoViewer
        isOpen={showVideoViewer}
        onClose={() => {
          setShowVideoViewer(false);
          setSelectedVideo(null);
        }}
        video={selectedVideo}
        onDownload={(videoId) => {
          // Handle download for videos in course details
          if (selectedCourse && selectedCourse.videos) {
            const video = selectedCourse.videos.find(v => v.id === videoId);
            if (video && video.is_downloadable) {
              // Call download API
              learnerDownloadAPI.getDownloadUrl('video', videoId)
                .then(response => {
                  if (response.data.download_url) {
                    window.open(response.data.download_url, '_blank');
                  }
                })
                .catch(error => {
                  showToast('error', 'Failed to download video');
                });
              }
          }
        }}
      />

      {/* File Viewer */}
      <FileViewer
        isOpen={showFileViewer}
        onClose={() => {
          setShowFileViewer(false);
          setSelectedFile(null);
        }}
        file={selectedFile}
      />

      {/* Purchase Course Modal */}
      <PurchaseCourseModal
        isOpen={showPurchaseModal}
        onClose={() => {
          setShowPurchaseModal(false);
          setCourseTourchase(null);
        }}
        course={courseToPurchase}
        onSuccess={(courseId) => {
          handlePurchaseSuccess(courseId);
          setShowPurchaseModal(false);
          setCourseTourchase(null);
          fetchCourses(); // Refresh courses to update owned status
        }}
      />
    </DashboardLayout>
  );
};

export default CourseBrowsePage;
